import { useQuery } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { unauthHttpRequest } from "@fmc-digital/auth-api";
import type { DefaultObject } from "@fmc-digital/auth-api";
import { useTokenReadiness } from "./useTokenReadiness";

/**
 * Fetch server time from T1 gateway
 * Returns server timestamp to avoid relying on client time
 */
const fetchServerTime = async (signal?: AbortSignal): Promise<number> => {
  try {
    const config: AxiosRequestConfig = {
      url: `/servertime?isHeadersPassed=true`,
      method: "GET",
      signal
    };

    const response = await unauthHttpRequest<DefaultObject>(config);

    // servicing-login reads `timestamp`; the generated client also documents `currentDateTime`.
    const serverTimeValue = (response?.timestamp ?? response?.currentDateTime) as
      | string
      | number
      | undefined;

    if (serverTimeValue !== undefined && serverTimeValue !== null) {
      const serverTime =
        typeof serverTimeValue === "number" ? serverTimeValue : new Date(serverTimeValue).getTime();

      // Validate the timestamp is reasonable (not NaN and not too far in past/future)
      if (!Number.isNaN(serverTime) && serverTime > 0) {
        return serverTime;
      }

      console.warn('[ServerTime] Invalid server time value:', serverTimeValue, serverTime);
    }

    // Do not fall back to Date.now() here: that would silently report the
    // device clock as the server clock and hide a real clock-skew banner.
    console.warn('[ServerTime] No valid server time in response');
    throw new Error("No valid server time in response");
  } catch (error) {
    if (error instanceof Error && error.message === "No valid server time in response") {
      throw error;
    }
    console.warn('[ServerTime] Failed to fetch server time:', error);
    throw error;
  }
};

/**
 * Hook to get server time from T1 gateway
 * 
 * This follows the servicing-login pattern of fetching server time
 * to avoid client time zone and clock skew issues
 */
export function useServerTime(enabled: boolean = true) {
  const { isTokenReady, hasBaseUrlt1 } = useTokenReadiness();

  const { data: serverTime, isLoading, error, refetch } = useQuery({
    queryKey: ["server-time"],
    queryFn: ({ signal }) => fetchServerTime(signal),
    enabled: enabled && hasBaseUrlt1 && isTokenReady, // Only enable when token is ready
    staleTime: 60 * 1000, // 1 minute - refresh server time periodically
    refetchInterval: 60 * 1000, // Refetch every minute to keep time in sync
    retry: 1,
  });

  // Displayed value falls back to the device clock so callers that only need
  // "some" timestamp (e.g. maintenance-window math) never see NaN. This
  // fallback must NOT be treated as a trustworthy server time - see hasServerTime.
  const safeServerTime = serverTime && !Number.isNaN(serverTime)
    ? serverTime
    : Date.now();

  return {
    serverTime: safeServerTime,
    isLoading,
    error,
    // Only true when the query itself returned a real, finite server timestamp.
    hasServerTime: typeof serverTime === "number" && !Number.isNaN(serverTime),
    refetch,
  };
}
